import React, { useEffect } from "react";
import { Route, useHistory, Redirect } from "react-router";
import Layout from "./containers/Layout";
import UmbracoPage from "./containers/UmbracoPage";
import { ThemeProvider, DefaultTheme } from "styled-components";
import ScrollToTop from "./hooks/ScrollToTop";
import "./tailwind.css";
import { isBrowserContext, isDevelopment } from "./util/ContextHelper";
import { useSelector } from "react-redux";
import { AppState } from "./store";
import { CurrentPageState } from "./store/page";
import Helmet from "react-helmet";
import { getGoogleAnalyticsId } from "./store/domain";
import "animate.css/animate.css";
import TixlyLoader from "./aat/components/Tixly/TixlyLoader";

export const theme: DefaultTheme = {
    backgroundColor: "#fff",
    color: "#000",
};

const App: React.FC = () => {
    const history = useHistory();
    const pageState = useSelector<AppState, CurrentPageState>((state) => state.page);
    const trackingCode = useSelector<AppState, string | undefined>(getGoogleAnalyticsId);

    useEffect(() => {
        if (isBrowserContext()) {
            if (trackingCode) {
                import("react-ga").then(({ default: ReactGA }) => {
                    // To debug use chrome extension: google-analytics-debugger
                    // Download here: https://chrome.google.com/webstore/detail/google-analytics-debugger/jnkmfdileelhofjcijamephohjechhna/related
                    ReactGA.initialize(trackingCode, {
                        debug: isDevelopment(),
                    });

                    if (history) {
                        // Trigger for the current page
                        const location = history.location;
                        ReactGA.set({ page: location.pathname + location.search + location.hash });
                        ReactGA.pageview(location.pathname);

                        // Trigget for all other pages after this
                        history.listen((location) => {
                            ReactGA.set({ page: location.pathname + location.search + location.hash });
                            ReactGA.pageview(location.pathname);
                        });
                    }
                });
            }
        }
    }, [history, trackingCode]);

    const renderRedirect = () => {
        if (pageState.redirectUrl) {
            const redirectUrl = new URL(pageState.redirectUrl).pathname;
            return <Redirect to={redirectUrl} />;
        }
        return null;
    };

    return (
        <ThemeProvider theme={theme}>
            <Helmet>
                <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
                <link rel="apple-touch-icon" sizes="180x180" href="/favicon/apple-touch-icon.png" />
                <link rel="icon" type="image/png" sizes="32x32" href="/favicon/favicon-32x32.png" />
                <link rel="icon" type="image/png" sizes="16x16" href="/favicon/favicon-16x16.png" />
                <link rel="manifest" href="/favicon/site.webmanifest" />
                <link rel="mask-icon" href="/favicon/safari-pinned-tab.svg" color="#000000" />
                <meta name="msapplication-TileColor" content="#000000" />
                <meta name="theme-color" content="#ffffff" />
            </Helmet>
            <Layout>
                {renderRedirect()}
                <Route path="*" component={UmbracoPage} />
            </Layout>
            <TixlyLoader />
            <ScrollToTop />
        </ThemeProvider>
    );
};

export default App;
